import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import UserService from '../../services/UserService';
import AddUserModal from '../formModal/AddUserModal';
import PaginationComponent from '../common/PaginationComponent';
import ChangePasswordModal from '../formModal/ChangePasswordModal';

const UserTable = ({loginUser}) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0)
    const [size, setSize] = useState(10);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, [currentPage, size]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await UserService.getAllUsers(currentPage, size);
            setUsers(response.content || []);
            setTotalPages(response.totalPages);
            setTotalUsers(response.totalElements);
        } catch (error) {
            toast.error(error.message || 'Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await UserService.deleteUser(id);
            toast.success('User deleted successfully.');
            fetchUsers();
        } catch (error) {
            toast.error(error.message || 'Failed to delete user.');
        }
    };

    const convertUTCToLocal = (utcDateString) => {
        if (!utcDateString) return 'N/A'; // Handle null or undefined cases
        const localDate = new Date(utcDateString);
        return localDate.toLocaleString(); // Formats the date and time to the local timezone
    };

    const handleDeactivate = async (id) => {
        if (!window.confirm('Are you sure you want to deactivate this user?')) return;
        try {
            await UserService.deactivateUser(id); // Call your API function to deactivate the user
            toast.success('User deactivated successfully.');
            fetchUsers();
        } catch (error) {
            toast.error(error.message || 'Failed to deactivate user.');
        }
    };

    const handleActivate = async (id) => {
        if (!window.confirm('Are you sure you want to activate this user?')) return;
        try {
            await UserService.activateUser(id); // Call your API function to activate the user
            toast.success('User activated successfully.');
            fetchUsers();
        } catch (error) {
            toast.error(error.message || 'Failed to activate user.');
        }
    };

    const handleChangePassword = (user) => {
        setSelectedUser(user);
        setShowChangePasswordModal(true);
    };

    return (
        <div className="table-responsive">
            <div className="mb-3">
                <strong>Total Users: {totalUsers}</strong>
            </div>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Role</th>
                            <th>Designation</th>
                            <th>Last Login</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user, index) => (
                            <tr key={user.id}>
                                <td>{index + 1}</td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.phone}</td>
                                <td>{user.role}</td>
                                <td>{user.designation}</td>
                                <td>{convertUTCToLocal(user.lastLogin)}</td>
                                <td>
                                    <span
                                        className={`badge ${user.status ? 'bg-success' : 'bg-danger'
                                            }`}
                                    >
                                        {user.status ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-light btn-sm dropdown-toggle"
                                            type="button"
                                            id={`dropdownMenuButton${user.id}`}
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            disabled={loginUser?.role!='ADMIN'}
                                        >
                                            <i className="fa fa-ellipsis"></i>
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton${user.id}`}>
                                            <li>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setShowEditModal(true);
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => handleChangePassword(user)}
                                                >
                                                    Change Password
                                                </button>
                                            </li>
                                            {user.status ? (
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleDeactivate(user.id)}
                                                    >
                                                        Deactivate
                                                    </button>
                                                </li>
                                            ) : (
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleActivate(user.id)}
                                                    >
                                                        Activate
                                                    </button>
                                                </li>
                                            )}
                                            <li>
                                                <button
                                                    className="dropdown-item text-danger"
                                                    onClick={() => handleDelete(user.id)}
                                                >
                                                    Delete
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {totalPages > 1 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
                />
            )}
            {showEditModal && (
                <AddUserModal
                    user={selectedUser}
                    onClose={() => {
                        setShowEditModal(false);
                        fetchUsers();
                    }}
                />
            )}
            {showChangePasswordModal && (
                <ChangePasswordModal
                    user={selectedUser}
                    onClose={() => setShowChangePasswordModal(false)}
                />
            )}
        </div>
    );
};

export default UserTable;
